import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Alerta, Botao, Campo, Cartao } from "@plantus/ui";
import { producao } from "@plantus/api-client";
import type {
  Fornecedor,
  MateriaPrima,
  OrigemDoLote,
  TipoDeMaterial,
  UnidadeDeMedida,
} from "@plantus/api-client";

const UNIDADES: { valor: UnidadeDeMedida; rotulo: string }[] = [
  { valor: "KG", rotulo: "kg — quilograma" },
  { valor: "G", rotulo: "g — grama" },
  { valor: "L", rotulo: "L — litro" },
  { valor: "ML", rotulo: "mL — mililitro" },
  { valor: "UN", rotulo: "un — unidade" },
];

/**
 * A primeira parte do código do lote — `MP`-COP-318425.
 *
 * A palavra vem junto da sigla porque quem nunca viu `MT` não deve ter de
 * adivinhar. É o mesmo par que sai impresso na etiqueta: selo com a sigla,
 * palavra ao lado.
 */
const TIPOS: { valor: TipoDeMaterial; rotulo: string }[] = [
  { valor: "MP", rotulo: "MP — matéria-prima" },
  { valor: "EX", rotulo: "EX — extrato" },
  { valor: "MT", rotulo: "MT — manteiga" },
  { valor: "SB", rotulo: "SB — sobra" },
];

interface Props {
  empresaId: string;
  aoReceber(loteId: string): void;
  aoCancelar(): void;
}

/**
 * O recebimento.
 *
 * <p>Repare no que <b>não</b> tem: campo de situação e campo de unidade.
 *
 * <p>A situação não existe porque todo material entra em quarentena — não é
 * escolha de quem digita. A unidade vem da matéria-prima cadastrada: digitá-la
 * a cada entrada é como o mesmo material acaba com {@code kg} numa nota e
 * {@code g} na seguinte, e aí somar o estoque exige adivinhar qual estava
 * certa.
 */
export function FormularioDeRecebimento({ empresaId, aoReceber, aoCancelar }: Props) {
  const [materiasPrimas, definirMateriasPrimas] = useState<MateriaPrima[]>([]);
  const [fornecedores, definirFornecedores] = useState<Fornecedor[]>([]);

  const [materiaPrima, definirMateriaPrima] = useState("");
  // A maioria do que entra vem da fazenda do grupo — é o padrão.
  const [origem, definirOrigem] = useState<OrigemDoLote>("PROPRIA");
  const [fornecedor, definirFornecedor] = useState("");
  const [loteDoFornecedor, definirLoteDoFornecedor] = useState("");
  const [notaFiscal, definirNotaFiscal] = useState("");
  const [quantidade, definirQuantidade] = useState("");
  // Um é o caso comum — um tambor, um saco — e é o padrão de quem não mexe
  // no campo. Zero não existe: material recebido está guardado em alguma
  // coisa, e essa coisa recebe etiqueta.
  const [volumes, definirVolumes] = useState("1");
  /*
   * ⚠️ MARCADO POR PADRÃO, e a API faz o contrário — de propósito.
   *
   * O servidor não inventa por omissão: pedido sem o campo grava ocupação SEM
   * recipiente, que é a verdade quando ninguém disse nada. A TELA propõe, porque
   * no galpão de hoje o tambor sem plaqueta é o caso comum — o parque está 100 %
   * sem cadastro (ADR-0012 item 6).
   *
   * Quem desmarca é quem sabe que aqueles tambores JÁ TÊM plaqueta: cunhar
   * identidade nova para um tambor que já tem uma criaria dois números para o
   * mesmo objeto físico, e a plaqueta antiga passaria a apontar para nada.
   */
  const [identificarRecipientes, definirIdentificarRecipientes] = useState(true);
  const [recebidoEm, definirRecebidoEm] = useState(() => new Date().toISOString().slice(0, 10));
  const [validade, definirValidade] = useState("");

  const [novaMateriaPrima, definirNovaMateriaPrima] = useState(false);
  const [mpCodigo, definirMpCodigo] = useState("");
  const [mpNome, definirMpNome] = useState("");
  const [mpTipo, definirMpTipo] = useState<TipoDeMaterial>("MP");
  const [mpEspecie, definirMpEspecie] = useState("");
  const [mpUnidade, definirMpUnidade] = useState<UnidadeDeMedida>("KG");

  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  useEffect(() => {
    void producao.materiasPrimas(empresaId).then(definirMateriasPrimas).catch(() => undefined);
    void producao.fornecedores(empresaId).then(definirFornecedores).catch(() => undefined);
  }, [empresaId]);

  const selecionada = materiasPrimas.find((mp) => mp.id === materiaPrima);

  async function cadastrarMateriaPrima() {
    definirErro(null);
    try {
      const criada = await producao.cadastrarMateriaPrima(
        empresaId, mpCodigo, mpNome, mpTipo, mpEspecie, mpUnidade,
      );
      definirMateriasPrimas((atuais) => [...atuais, criada]);
      definirMateriaPrima(criada.id);
      definirNovaMateriaPrima(false);
      definirMpCodigo("");
      definirMpNome("");
      definirMpEspecie("");
    } catch {
      definirErro(
        "Não foi possível cadastrar a matéria-prima. A espécie tem exatamente três letras.",
      );
    }
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    definirErro(null);
    definirEnviando(true);
    try {
      const deTerceiro = origem === "TERCEIRO";
      const { id } = await producao.receber({
        empresa: empresaId,
        materiaPrima,
        origem,
        fornecedor: deTerceiro ? fornecedor : null,
        loteDoFornecedor: deTerceiro && loteDoFornecedor !== "" ? loteDoFornecedor : null,
        notaFiscal: deTerceiro && notaFiscal !== "" ? notaFiscal : null,
        quantidade,
        volumes: Math.max(1, Number(volumes) || 1),
        recebidoEm,
        validade,
        observacao: null,
        identificarRecipientes,
      });
      aoReceber(id);
    } catch (falha) {
      definirErro(
        falha instanceof Error && "codigo" in falha
          ? ((falha as { message: string }).message ?? "Não foi possível registrar o recebimento.")
          : "Não foi possível registrar o recebimento.",
      );
    } finally {
      definirEnviando(false);
    }
  }

  return (
    <Cartao titulo="Receber material">
      <h2 className="recebimento__titulo">Receber material</h2>

      <Alerta tom="informacao">
        Todo material entra em <strong>quarentena</strong> e permanece nela. Liberar
        exige assinatura de quem tem o papel — e essa parte do sistema ainda não
        existe.
      </Alerta>

      <form onSubmit={enviar} noValidate className="recebimento">
        {erro !== null && <Alerta tom="erro">{erro}</Alerta>}

        {/* ---------------- matéria-prima ---------------- */}
        <div className="recebimento__linha">
          <label className="recebimento__rotulo" htmlFor="materia-prima">
            Matéria-prima <span aria-hidden="true">*</span>
          </label>
          <div className="recebimento__combinado">
            <select
              id="materia-prima"
              required
              value={materiaPrima}
              onChange={(e) => definirMateriaPrima(e.target.value)}
            >
              <option value="">Selecione…</option>
              {materiasPrimas.map((mp) => (
                <option key={mp.id} value={mp.id}>
                  {mp.codigo} · {mp.nome}
                </option>
              ))}
            </select>
            <Botao
              type="button"
              aparencia="secundaria"
              onClick={() => definirNovaMateriaPrima((a) => !a)}
            >
              {novaMateriaPrima ? "Cancelar" : "+ Nova"}
            </Botao>
          </div>
        </div>

        {novaMateriaPrima && (
          <div className="recebimento__aninhado">
            <Campo rotulo="Código interno" value={mpCodigo}
                   onChange={(e) => definirMpCodigo(e.target.value)} />
            <Campo rotulo="Nome" value={mpNome}
                   onChange={(e) => definirMpNome(e.target.value)} />

            {/* ── tipo e espécie: as duas primeiras partes do código do lote ──
                Eles são da MATÉRIA-PRIMA, não do recebimento — mesma razão da
                unidade de medida. Digitados a cada entrada, o mesmo insumo
                sairia como MP-COP numa nota e EX-COP na seguinte, e o código
                deixaria de dizer o que diz. */}
            <div className="ui-campo">
              <label className="ui-campo__rotulo" htmlFor="mp-tipo">
                Tipo de material
              </label>
              <select id="mp-tipo" className="ui-campo__entrada" value={mpTipo}
                      onChange={(e) => definirMpTipo(e.target.value as TipoDeMaterial)}>
                {TIPOS.map((t) => (
                  <option key={t.valor} value={t.valor}>{t.rotulo}</option>
                ))}
              </select>
              <p className="ui-campo__dica">
                A sigla sai vazada num quadrado preto na etiqueta — é o que se
                reconhece a três metros, com óleo, extrato e manteiga da mesma
                planta lado a lado.
              </p>
            </div>

            <Campo
              rotulo="Espécie (3 letras)"
              value={mpEspecie}
              maxLength={3}
              onChange={(e) => definirMpEspecie(e.target.value.toUpperCase())}
              dica="COP copaíba · ALG algodão · VET vetiver. Vai no meio do código do lote: MP-COP-318425."
            />

            <div className="ui-campo">
              <label className="ui-campo__rotulo" htmlFor="mp-unidade">
                Unidade de medida
              </label>
              <select id="mp-unidade" className="ui-campo__entrada" value={mpUnidade}
                      onChange={(e) => definirMpUnidade(e.target.value as UnidadeDeMedida)}>
                {UNIDADES.map((u) => (
                  <option key={u.valor} value={u.valor}>{u.rotulo}</option>
                ))}
              </select>
              <p className="ui-campo__dica">
                A unidade é da matéria-prima, não do recebimento — é o que impede
                o mesmo material entrar em kg e depois em g.
              </p>
            </div>
            <Botao type="button" onClick={() => void cadastrarMateriaPrima()}>
              Cadastrar matéria-prima
            </Botao>
          </div>
        )}

        {/* ---------------- origem ---------------- */}
        <fieldset className="recebimento__origem">
          <legend className="recebimento__rotulo">De onde veio</legend>
          <label>
            <input
              type="radio"
              name="origem"
              checked={origem === "PROPRIA"}
              onChange={() => definirOrigem("PROPRIA")}
            />
            <span>
              Fazenda própria
              <small>Sem fornecedor, sem nota fiscal — o lote interno é o lote.</small>
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="origem"
              checked={origem === "TERCEIRO"}
              onChange={() => definirOrigem("TERCEIRO")}
            />
            <span>
              Comprado de terceiro
              <small>Exige fornecedor e o lote dele — é o elo com a origem.</small>
            </span>
          </label>
        </fieldset>

        {/* ---------------- fornecedor: só quando veio de fora ---------------- */}
        {/*
          * Só os QUALIFICADOS, e nenhum caminho para cadastrar aqui.
          *
          * Havia um "+ Novo fornecedor" nesta tela: quem estava recebendo
          * material criava o fornecedor na hora, que é exatamente o que a
          * qualificação existe para impedir. Comprar de fornecedor não
          * qualificado é desvio de BPF.
          *
          * A lista vazia é resposta legítima, e a tela diz o que fazer em vez
          * de oferecer o atalho.
          */}
        {origem === "TERCEIRO" && (
          fornecedores.length === 0 ? (
            <Alerta tom="atencao">
              Nenhum fornecedor qualificado para esta empresa. A qualificação é
              de <strong>Assuntos Regulatórios</strong> — material só entra
              depois dela.
            </Alerta>
          ) : (
            <div className="recebimento__linha">
              <label className="recebimento__rotulo" htmlFor="fornecedor">
                Fornecedor <span aria-hidden="true">*</span>
              </label>
              <select id="fornecedor" required value={fornecedor}
                      onChange={(e) => definirFornecedor(e.target.value)}>
                <option value="">Selecione…</option>
                {fornecedores.map((f) => (
                  <option key={f.id} value={f.id}>{f.nome}</option>
                ))}
              </select>
              <p className="ui-campo__dica">
                Só aparecem os qualificados por Assuntos Regulatórios.
              </p>
            </div>
          )
        )}

        {/* ---------------- o lote ---------------- */}
        <div className="recebimento__grade">
          {origem === "TERCEIRO" && (
            <>
              <Campo rotulo="Lote do fornecedor" required value={loteDoFornecedor}
                     onChange={(e) => definirLoteDoFornecedor(e.target.value)}
                     dica="O que veio no rótulo do fornecedor — é o elo com a origem." />

              <Campo rotulo="Nota fiscal" value={notaFiscal}
                     onChange={(e) => definirNotaFiscal(e.target.value)} />
            </>
          )}

          <Campo
            rotulo={`Quantidade${selecionada ? ` (${simbolo(selecionada.unidade)})` : ""}`}
            required
            inputMode="decimal"
            value={quantidade}
            onChange={(e) => definirQuantidade(e.target.value.replace(",", "."))}
            dica={selecionada === undefined
              ? "A unidade aparece quando você escolher a matéria-prima."
              : undefined}
          />

          {/* ── volumes ──────────────────────────────────────────────────
              Peso total + quantos recipientes; o sistema divide.

              🔴 ATALHO DECLARADO DO MVP. No definitivo cada volume é
              REGISTRO PRÓPRIO, com o peso individual conferido na balança —
              que é o que a conferência de recebimento de fato faz. Aqui só a
              CONTAGEM é registrada, e o peso de cada volume é a divisão do
              total: A DIVISÃO EXIBIDA É CALCULADA, NÃO PESADA.

              A dica diz isso a quem preenche, porque quem preenche é quem vai
              conferir na balança depois e precisa saber que a etiqueta traz
              uma média. */}
          <Campo
            rotulo="Volumes"
            type="number"
            min={1}
            required
            value={volumes}
            onChange={(e) => definirVolumes(e.target.value)}
            dica={
              selecionada !== undefined && quantidade !== "" && Number(volumes) > 1
                ? `Cada volume sai na etiqueta com ${divisaoAproximada(quantidade, volumes)} ${simbolo(selecionada.unidade)} — CALCULADO, não pesado.`
                : "Quantos recipientes. Cada um recebe uma etiqueta numerada."
            }
          />

          {/* ── a identidade dos tambores ─────────────────────────────────
              IDENTIDADE, NÃO DOSSIÊ. Tipo, construção, capacidade, massa
              máxima e tara NÃO são pedidos aqui: dado de ativo digitado com
              o caminhão esperando é dado que ninguém confia depois — e é o
              dado que o portão de reuso vai ler para decidir. O que sai
              daqui é um número para escrever na plaqueta. */}
          <div className="ui-campo recebimento__identificacao">
            <label className="recebimento__caixa">
              <input
                type="checkbox"
                checked={identificarRecipientes}
                onChange={(e) => definirIdentificarRecipientes(e.target.checked)}
              />
              <span>Estes tambores ainda não têm plaqueta</span>
            </label>
            <p className="ui-campo__dica">
              Cria a <strong>identidade</strong> de um recipiente para cada volume
              — <code>R-0047</code>, o número que vai na plaqueta. A ficha técnica
              (tipo, capacidade, massa máxima, tara) é preenchida depois, em{" "}
              <strong>Recipientes</strong>. Desmarque se os tambores já têm
              plaqueta.
            </p>
          </div>

          <Campo rotulo="Recebido em" type="date" required value={recebidoEm}
                 onChange={(e) => definirRecebidoEm(e.target.value)} />

          <Campo rotulo="Validade" type="date" required value={validade}
                 onChange={(e) => definirValidade(e.target.value)}
                 dica="Se o fornecedor não informa, use a data de reanálise. Vazio não passa." />
        </div>

        <div className="recebimento__acoes">
          <Botao type="submit" carregando={enviando}>Receber e gerar etiqueta</Botao>
          <Botao type="button" aparencia="texto" onClick={aoCancelar}>Cancelar</Botao>
        </div>
      </form>
    </Cartao>
  );
}

function simbolo(unidade: UnidadeDeMedida): string {
  return { KG: "kg", G: "g", L: "L", ML: "mL", UN: "un" }[unidade];
}

/**
 * A prévia da divisão, **só para a dica do formulário**.
 *
 * <p>Ela é aproximada e é assim de propósito: quem divide de verdade é o
 * servidor, em `BigDecimal`, e é o número dele que vai impresso. Repetir a
 * conta aqui em ponto flutuante para exibir dois números diferentes na mesma
 * tela seria pior que não mostrar nada — mas ver a ordem de grandeza **antes**
 * de gravar é o que pega o erro de digitar 200 volumes em vez de 20.
 */
function divisaoAproximada(quantidade: string, volumes: string): string {
  const total = Number(quantidade);
  const partes = Number(volumes);
  if (!Number.isFinite(total) || !Number.isFinite(partes) || partes < 1) {
    return "—";
  }
  return (total / partes).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });
}
